/* Copyright (c) 2020-present CloudIO Inc */

import {
  StateXGetter,
  useStateXAction,
  useStateXAddToUndo,
  useStateXAtom,
  useStateXGetter,
  useStateXSelectorSetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Divider,
  Input,
  ListItemIcon,
  ListItemText,
  Theme,
  Menu,
  MenuItem,
  Typography,
  MenuList,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AnyItem,
  AnyManager,
  getAllManagers,
  getManager,
  Item,
  ItemID,
  ItemType,
  Page,
  Platform,
  PropertyDefinition,
} from 'componentregistry';
import { EmbedPageItem } from 'componentregistry/components/EmbedPage/EmbedPage';
import {
  autoPopulateChildrenAction,
  deleteAction,
  deleteChildrenAction,
  deleteItem,
  duplicateAction,
  pasteAction,
  replaceItemAction,
} from 'designer/state';
import { useIORef } from 'designer/useIORef';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import {
  contextMenuAtom,
  treeViewFilterMatchCaseAtom,
  treeViewFilterMatchWholeWordAtom,
  treeViewFilterAtom,
} from 'layout/Sidebar/atoms';
import { TreeTypeDesigner } from 'layout/Sidebar/panels/TreeView/state';
import { usePlatform } from 'platform/PlatformHooks';
import { GoToDataSourceMenu } from 'platform/RuntimeDeveloperContextMenu';
import React, { useRef, useState, useLayoutEffect } from 'react';
import {
  createItem,
  itemWithDefaultValues,
  makeItem,
} from 'registry/Components';
import assert from 'utils/assert';
import { useUpdateOnHMR } from 'utils/hmr';
import { isNotEmpty } from 'utils/isEmpty';
import { MenuItemPulse } from 'utils/Pulse';
import { getAllowedParentComponents } from '../layout/Sidebar/panels/ComponentsView/ComponentHelp';
import { activeItemSelector } from './activeItemSelector';
import { copyItemAtom, designPath, embeddedPagesAtom, pageAtom } from './atoms';

const useStyles = makeStyles((theme: Theme) => ({
  menu: {
    paddingTop: '0px',
    '& $searchFocus': {
      backgroundColor: 'transparent',
    },
  },
  itemText: {
    maxWidth: 360,
    whiteSpace: 'normal',
    '& > span.MuiListItemText-primary': {
      whiteSpace: 'nowrap',
    },
  },
  menuItem: {
    minWidth: 180,
  },
  icon: {
    marginRight: 12,
  },
  search: {
    border: 0,
    width: '100%',
    flex: 1,
    paddingRight: 12,
    minWidth: 200,
  },
  searchFocus: { display: 'flex', alignItems: 'center' },
  embed: {
    fontSize: '0.8em',
    color: theme.palette.text.secondary,
  },
}));

function findDataSourceItemId(
  page: Page,
  children: string[],
  alias: string,
): string | null {
  for (let i = 0; i < children.length; i++) {
    const id = children[i];
    const itm = page[id];
    if (itm) {
      if (
        (itm.type === 'DataStore' ||
          itm.type === 'TransientDataStore' ||
          itm.type === 'LocalDataStore' ||
          itm.type === 'ReadOnlyDataStore') &&
        // @ts-ignore alias
        itm.props.alias === alias
      ) {
        return id;
      }
      if (itm.type === 'Folder') {
        const itemId = findDataSourceItemId(page, itm.children, alias);
        if (itemId) return itemId;
      }
    }
  }
  return null;
}

export function canDuplicate(
  platform: Platform,
  appUid: string,
  pageId: string,
  item: AnyItem,
  get: StateXGetter,
) {
  let result: string | boolean = false;
  if (!pageId) {
    return false;
  }
  const page = get(pageAtom, { params: { appUid, pageId } });
  if (!page) {
    throw Error(`Missing page w/ id ${pageId}`);
  }
  const mgr = getManager(item.type);
  if (mgr && page && item && page[item.parent]) {
    const parent = page[item.parent];
    const m = getManager(parent.type);
    if (m) {
      // Use mgr.type instead of item.type as the item.type may have been refactored
      // e.g. GridContainer -> RowPanel
      const isChildAllowed = m.isChildAllowed(page, parent, mgr.type, {
        appUid,
        pageId,
        itemId: parent.itemId,
        mgr: m,
        platform,
      });
      result =
        isChildAllowed === true &&
        mgr.isAllowedInsideParent(page, parent, {
          appUid,
          pageId,
          itemId: parent.itemId,
          mgr,
          platform,
        }) === true;
    }
  }
  return result;
}

const defaultSize = { width: 0, minHeight: 0 };

export default function ContextMenu({
  appUid,
  pageId: apageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement | null {
  const inputRef = useRef<HTMLInputElement>(null);
  const platform = usePlatform();
  const [{ pageId, itemId, orphan, dsAlias, ...point }, setPoint] =
    useStateXAtom(contextMenuAtom);
  const duplicate = useStateXAction(duplicateAction);
  const paste = useStateXAction(pasteAction);
  const [copyItem, setCopyItem] = useStateXAtom(copyItemAtom);
  const deleteItemFn = useStateXAction(deleteAction);
  const replaceItem = useStateXAction(replaceItemAction);
  const deleteChildrenActionFn = useStateXAction(deleteChildrenAction);
  const [maxCCListHeight, setMaxCCListHeight] = useState(200);
  const autoPopulateChildrenActionFn = useStateXAction(
    autoPopulateChildrenAction,
  );
  const setActiveItem = useStateXSelectorSetter(activeItemSelector, {
    params: { appUid, treename: TreeTypeDesigner, pageId },
    props: platform,
  });
  const [filter, setFilter] = useState('');
  const [size, setSize] = useState(defaultSize);

  const classes = useStyles();
  const get = useStateXGetter();
  const set = useStateXSetter();
  const addToUndo = useStateXAddToUndo('io');
  const ref = useIORef(null);
  const listRef = useIORef(null);
  const [, wh] = useWindowSize();
  useUpdateOnHMR();

  useLayoutEffect(() => {
    const refUnsubscribe = ref.subscribe((el) => {
      if (el instanceof HTMLElement && listRef.current instanceof HTMLElement) {
        const h = wh - el.offsetHeight - 40;
        setMaxCCListHeight(listRef.current.offsetHeight + h);
      }
    });
    const listRefUnsubscribe = listRef.subscribe((el) => {
      if (el instanceof HTMLElement && ref.current instanceof HTMLElement) {
        const h = wh - ref.current.offsetHeight - 40;
        setMaxCCListHeight(el.offsetHeight + h);
      }
    });
    return () => {
      refUnsubscribe();
      listRefUnsubscribe();
    };
  }, [listRef, ref, wh]);

  if (point.left === 0) {
    return null;
  }

  function close() {
    setFilter('');
    setSize(defaultSize);
    setPoint((p) => ({ ...p, left: 0, top: 0 }));
  }

  const page = get(pageAtom, { params: { appUid, pageId } });
  if (!page) {
    return null;
  }
  const item: AnyItem = page[itemId];
  if (!item) {
    return null;
  }
  const mgr = getManager(item.type);

  const allComponents: {
    allowed: string | boolean;
    m: AnyManager;
  }[] = orphan
    ? []
    : getAllManagers()
        .filter((m) => !m.deprecated)
        .map((m): { allowed: string | boolean; m: AnyManager } => {
          const isChildAllowed = mgr.isChildAllowed(page, item, m.type, {
            appUid,
            pageId,
            itemId: item.itemId,
            mgr,
            platform,
          });
          if (typeof isChildAllowed === 'string' || !isChildAllowed) {
            return { allowed: isChildAllowed, m };
          }
          const isAllowedInsideParent = m.isAllowedInsideParent(page, item, {
            appUid,
            pageId,
            itemId: item.itemId,
            mgr: m,
            platform,
          });
          if (typeof isAllowedInsideParent === 'string') {
            return { allowed: isAllowedInsideParent, m };
          }
          return {
            allowed: isChildAllowed && isAllowedInsideParent,
            m,
          };
        });

  const allowedComponents = allComponents.filter(
    (row) => row.allowed !== false,
  );

  const otherComponents = allComponents.filter(
    (row) =>
      row.allowed === false &&
      row.m.managerType ===
        (mgr.managerType === 'Component' ? 'Component' : 'Action') &&
      !row.m.hideInComponentsList &&
      !row.m.deprecated,
  );

  let allowedParentComponents: {
    allowed: string | boolean;
    m: AnyManager;
    subItem?: ItemType;
  }[] = [];
  const parent = page[item.parent];
  // orphan item may not have the parent as part of this page
  if (!orphan && parent) {
    const parentMgr = getManager(parent.type);
    allowedParentComponents = getAllManagers()
      .filter((m) => !m.deprecated)
      .map(
        (
          m,
        ): {
          allowed: string | boolean;
          m: AnyManager;
          subItem?: ItemType;
        } => {
          const emptyParent = { ...parent, children: [] };
          const isChildAllowed = parentMgr.isChildAllowed(
            page,
            emptyParent,
            m.type,
            {
              appUid,
              pageId,
              itemId: emptyParent.itemId,
              mgr: parentMgr,
              platform,
            },
          );
          if (typeof isChildAllowed === 'string' || !isChildAllowed) {
            // not allowed inside the parent item
            return { allowed: isChildAllowed, m };
          }
          const isAllowedInsideParent = m.isAllowedInsideParent(page, parent, {
            appUid,
            pageId,
            itemId: parent.itemId,
            mgr: m,
            platform,
          });
          if (
            typeof isAllowedInsideParent === 'string' ||
            !isAllowedInsideParent
          ) {
            // not allowed inside the parent item
            return { allowed: isAllowedInsideParent, m };
          }
          const collect: Record<ItemID, AnyItem> = {};
          const embedItemId = makeItem(
            platform,
            page,
            parent.itemId,
            m.type,
            collect,
          );
          if (!embedItemId) {
            return { allowed: false, m };
          }
          const newItem: AnyItem = collect[embedItemId];
          const emptyNewItem = { ...newItem, children: [] };
          const isAllowed = m.isChildAllowed(
            { ...page, [emptyNewItem.itemId]: emptyNewItem },
            emptyNewItem,
            item.type,
            {
              appUid,
              pageId,
              itemId: emptyNewItem.itemId,
              mgr: m,
              platform,
            },
          );
          if (typeof isChildAllowed === 'string' || !isAllowed) {
            // check if the item can be added under any of the child of m (e.g. Card Content inside Card)
            const _child = newItem.children.find((child) => {
              const childItem: AnyItem = collect[child];
              const childMgr = getManager(childItem.type);
              const emptyChildItem = { ...childItem, children: [] };
              return (
                childMgr.isChildAllowed(page, emptyChildItem, item.type, {
                  appUid,
                  pageId,
                  itemId: emptyChildItem.itemId,
                  mgr: childMgr,
                  platform,
                }) === true
              );
            });
            return {
              allowed: !!_child,
              m,
              subItem: _child ? collect[_child].type : undefined,
            };
          }

          const isAllowedInsideParent2 = mgr.isAllowedInsideParent(
            page,
            emptyNewItem,
            {
              appUid,
              pageId,
              itemId: emptyNewItem.itemId,
              mgr,
              platform,
            },
          );
          if (
            typeof isAllowedInsideParent2 === 'string' ||
            !isAllowedInsideParent2
          ) {
            // not allowed
            return { allowed: isAllowedInsideParent2, m };
          }

          return {
            allowed: true,
            m,
          };
        },
      )
      .filter((row) => row.allowed === true);
  }

  let replaceWithChild: AnyManager | null = null;
  let replaceWithGrandChild: AnyItem | null = null;
  // orphan item may not have the parent as part of this page
  if (!orphan && parent && item.children?.length === 1) {
    const parentMgr = getManager(parent.type);
    const child = page[item.children[0]];
    const emptyParent = { ...parent, children: [] };
    const isChildAllowed = parentMgr.isChildAllowed(
      page,
      emptyParent,
      child.type,
      {
        appUid,
        pageId,
        itemId: emptyParent.itemId,
        mgr: parentMgr,
        platform,
      },
    );
    if (isChildAllowed === true) {
      const childMgr = getManager(child.type);
      if (
        childMgr.isAllowedInsideParent(page, emptyParent, {
          appUid,
          pageId,
          itemId: emptyParent.itemId,
          mgr: childMgr,
          platform,
        }) === true
      ) {
        replaceWithChild = getManager(child.type);
      }
    } else if (child.children?.length === 1) {
      // check if the child has a single grand child that can be used to replace the current item
      const grandChild = page[child.children[0]];
      const emptyGrandParent = { ...parent, children: [] };
      const isGrandChildAllowed = parentMgr.isChildAllowed(
        page,
        emptyGrandParent,
        grandChild.type,
        {
          appUid,
          pageId,
          itemId: emptyGrandParent.itemId,
          mgr: parentMgr,
          platform,
        },
      );
      if (isGrandChildAllowed === true) {
        const grandChildMgr = getManager(grandChild.type);
        if (
          grandChildMgr.isAllowedInsideParent(page, emptyGrandParent, {
            appUid,
            pageId,
            itemId: emptyGrandParent.itemId,
            mgr: grandChildMgr,
            platform,
          }) === true
        ) {
          replaceWithChild = getManager(grandChild.type);
          replaceWithGrandChild = grandChild;
        }
      }
    }
  }

  const dup = orphan
    ? false
    : canDuplicate(platform, appUid, pageId, item, get);

  let pasteAdjacentAllowed: string | boolean = false;
  let pasteInsideAllowed: string | boolean = false;
  const isPage = mgr.type === 'Page';
  if (copyItem) {
    const copyItemMgr = getManager(copyItem.type);
    pasteInsideAllowed =
      // Use copyItemMgr.type instead of copyItem.type as it may have been refactored
      // e.g. GridContainer -> RowPanel
      mgr.isChildAllowed(page, item, copyItemMgr.type, {
        appUid,
        pageId,
        itemId: item.itemId,
        mgr,
        platform,
      }) === true &&
      copyItemMgr.isAllowedInsideParent(page, item, {
        appUid,
        pageId,
        itemId: item.itemId,
        mgr: copyItemMgr,
        platform,
      }) === true;

    const m = getManager(parent.type);
    pasteAdjacentAllowed =
      !isPage &&
      m.isChildAllowed(page, parent, copyItemMgr.type, {
        appUid,
        pageId,
        itemId: parent.itemId,
        mgr: m,
        platform,
      }) === true &&
      copyItemMgr.isAllowedInsideParent(page, parent, {
        appUid,
        pageId,
        itemId: parent.itemId,
        mgr: copyItemMgr,
        platform,
      }) === true;
  }

  const styles = size.width ? size : undefined;

  const autoPopulateChildren =
    mgr.type === 'DataGrid' ||
    mgr.type === 'CanvasGrid' ||
    mgr.type === 'PivotTable' ||
    mgr.type === 'Form' ||
    mgr.type === 'SearchForm' ||
    mgr.type === 'SmartSearch';
  let embeddedPageId = '';
  if (item.type === 'EmbedPage') {
    const embeddedPageIdProp = (item as EmbedPageItem).props.embeddedPageId;
    if (embeddedPageIdProp.type === 'text') {
      embeddedPageId = embeddedPageIdProp.value;
    } else if (typeof embeddedPageIdProp === 'string') {
      embeddedPageId = embeddedPageIdProp as string;
    }
  }

  // @ts-ignore targetItemId
  const targetItemIdPropDef = mgr.props.targetItemId as PropertyDefinition;
  let selectTargetItemIdLabel = '';
  // @ts-ignore targetItemId
  const targetItemId = item.props.targetItemId;
  if (targetItemIdPropDef && targetItemId) {
    selectTargetItemIdLabel = `Select ${targetItemIdPropDef.label} [${targetItemId}]`;
  }

  // @ts-ignore alias
  const alias = item.props.alias ?? dsAlias;
  let aliasItemId: string | null = null;
  let aliasDS = '';
  let aliasDSType = '';
  if (alias) {
    aliasItemId = findDataSourceItemId(page, page.root.children, alias);
    if (aliasItemId) {
      const itm = page[aliasItemId] as Item<'DataStore'>;
      aliasDS = itm.props.dataSource;
      aliasDSType = getManager(itm.type).name;
    }
  }
  const filterLC = filter ? filter.toLowerCase() : undefined;
  const filteredComponents = allowedComponents.filter(
    (c) =>
      !filterLC ||
      c.m.name.toLowerCase().includes(filterLC) ||
      (filterLC === 'custom' && isNotEmpty(c.m.version)),
  );
  const filteredOtherComponents = filterLC
    ? otherComponents.filter(
        (c) =>
          c.m.name.toLowerCase().includes(filterLC) ||
          (filterLC === 'custom' && isNotEmpty(c.m.version)),
      )
    : undefined;
  let icon = mgr.icon;
  if (typeof icon === 'function') {
    icon = icon(itemWithDefaultValues(item));
  }
  let replaceableWithTypes: readonly ItemType[];
  if (typeof mgr.replaceableWith === 'function') {
    replaceableWithTypes = mgr.replaceableWith(itemWithDefaultValues(item));
  } else {
    replaceableWithTypes = mgr.replaceableWith;
  }
  replaceableWithTypes = replaceableWithTypes.filter(
    (itemType) => itemType !== mgr.type && !getManager(itemType).deprecated,
  );

  return (
    <Menu
      open
      className={classes.menu}
      onClose={close}
      anchorReference="anchorPosition"
      anchorPosition={point}
      MenuListProps={{ ref, style: styles, sx: { padding: 0 } }}
      BackdropProps={{ 'aria-label': 'Menu Backdrop' }}
    >
      <MenuItem key="name" className={classes.menuItem} disabled>
        <Icon
          className={classes.icon}
          iconType={icon?.iconType}
          iconName={icon?.iconName ?? ''}
        />
        {mgr.name}
      </MenuItem>
      <Divider key="ndivider" sx={{ margin: '0px !important' }} />
      {embeddedPageId && [
        <MenuItem
          data-testid="cm-goto-embedded-page"
          key="goto-embedded-page"
          className={classes.menuItem}
          onClick={() => {
            close();
            platform.setter(embeddedPagesAtom, (pages = []) =>
              pages.filter((p) => p !== embeddedPageId),
            );
            platform.goToPage(appUid, embeddedPageId);
          }}
        >
          <Icon className={classes.icon} iconName="copy" />
          Go To Page {embeddedPageId}
        </MenuItem>,
        <Divider key="spdivider" sx={{ margin: '0px !important' }} />,
      ]}
      {alias ? (
        <GoToDataSourceMenu
          appUid={appUid}
          dsAlias={alias}
          itemId={itemId}
          key="alias"
          onClose={close}
          pageId={pageId}
        />
      ) : null}
      {aliasItemId && aliasDS ? (
        itemId === aliasItemId ? (
          <MenuItem
            data-testid="cm-selectDs"
            key="selectDs"
            className={classes.menuItem}
            onClick={() => {
              close();
              platform.setter(treeViewFilterMatchCaseAtom, true, {
                params: { treename: 'designer' },
              });
              platform.setter(treeViewFilterMatchWholeWordAtom, true, {
                params: { treename: 'designer' },
              });
              platform.setter(treeViewFilterAtom, alias, {
                params: { treename: 'designer' },
              });
            }}
          >
            <Icon className={classes.icon} iconName="search" />
            Search Usage of {aliasDSType} [{aliasDS}]
          </MenuItem>
        ) : (
          <MenuItem
            data-testid="cm-selectDs"
            key="selectDs"
            className={classes.menuItem}
            onClick={() => {
              close();
              if (aliasItemId) {
                setActiveItem({
                  appUid,
                  pageId,
                  itemId: aliasItemId,
                  expand: true,
                  treename: TreeTypeDesigner,
                });
              }
            }}
          >
            <Icon className={classes.icon} iconName="external-link-square" />
            Select {aliasDSType} [{aliasDS}]
            <MenuItemPulse
              pulseKey="selectDs"
              pulseMessage={`Use this option to select the "${aliasDSType}" ${aliasDS} used by this component ${mgr.name}`}
            />
          </MenuItem>
        )
      ) : null}
      {targetItemId && selectTargetItemIdLabel ? (
        <MenuItem
          data-testid="cm-selectTargetItem"
          key="selectTargetItem"
          className={classes.menuItem}
          onClick={() => {
            close();
            setActiveItem({
              appUid,
              pageId,
              itemId: targetItemId,
              expand: true,
              treename: TreeTypeDesigner,
            });
          }}
        >
          <Icon className={classes.icon} iconName="external-link-square" />
          {selectTargetItemIdLabel}
        </MenuItem>
      ) : null}
      {!isPage && [
        <MenuItem
          data-testid="cm-selectParent"
          key="selectParent"
          className={classes.menuItem}
          onClick={() => {
            close();
            setActiveItem({
              appUid,
              pageId,
              itemId: item.parent,
              expand: true,
              treename: TreeTypeDesigner,
            });
          }}
        >
          <Icon className={classes.icon} iconName="expand" />
          Select Parent
        </MenuItem>,
        <Divider key="spdivider" sx={{ margin: '0px !important' }} />,
      ]}

      {!isPage && (
        <MenuItem
          data-testid="cm-copy"
          className={classes.menuItem}
          onClick={() => {
            close();
            if (get(designPath({ appUid, pageId: '$$$copy$$$' }))) {
              // cleanup dummy page $$$copy$$$ that is populated in deleteAction
              set(designPath({ appUid, pageId: '$$$copy$$$' }), undefined);
            }
            setCopyItem({ pageId, itemId: item.itemId, type: item.type });
          }}
        >
          <Icon className={classes.icon} iconName="copy" />
          Copy
        </MenuItem>
      )}
      {copyItem
        ? [
            <Divider key="top" sx={{ margin: '0px !important' }} />,
            <MenuItem
              data-testid="cm-paste-above"
              disabled={
                typeof pasteAdjacentAllowed === 'boolean'
                  ? !pasteAdjacentAllowed
                  : true
              }
              key="above"
              onClick={() => {
                addToUndo(
                  designPath({
                    appUid,
                    pageId,
                  }),
                  pageId,
                );
                paste({
                  appUid,
                  pageId,
                  itemId,
                  pasteAt: 'above',
                  platform,
                });
              }}
            >
              <Icon className={classes.icon} iconName="paste" />
              Paste Above
            </MenuItem>,
            <MenuItem
              data-testid="cm-paste-inside"
              key="inside"
              disabled={
                typeof pasteInsideAllowed === 'boolean'
                  ? !pasteInsideAllowed
                  : true
              }
              onClick={() => {
                addToUndo(
                  designPath({
                    appUid,
                    pageId,
                  }),
                  pageId,
                );
                paste({
                  appUid,
                  pageId,
                  itemId,
                  pasteAt: 'inside',
                  platform,
                });
              }}
            >
              <Icon className={classes.icon} iconName="paste" />
              Paste Inside
            </MenuItem>,
            <MenuItem
              data-testid="cm-paste-below"
              key="below"
              disabled={
                typeof pasteAdjacentAllowed === 'boolean'
                  ? !pasteAdjacentAllowed
                  : true
              }
              onClick={() => {
                addToUndo(
                  designPath({
                    appUid,
                    pageId,
                  }),
                  pageId,
                );
                paste({
                  appUid,
                  pageId,
                  itemId,
                  pasteAt: 'below',
                  platform,
                });
              }}
            >
              <Icon className={classes.icon} iconName="paste" />
              Paste Below
            </MenuItem>,
            <Divider key="bottom" sx={{ margin: '0px !important' }} />,
          ]
        : null}
      {!(isPage || orphan) && (
        <MenuItem
          data-testid="cm-duplicate"
          disabled={typeof dup === 'boolean' ? !dup : true}
          onClick={() => {
            close();
            addToUndo(
              designPath({
                appUid,
                pageId,
              }),
              pageId,
            );
            duplicate({
              appUid,
              pageId,
              itemId,
              platform,
            });
          }}
        >
          <Icon className={classes.icon} iconName="clone" />
          Duplicate
        </MenuItem>
      )}
      {replaceWithChild ? (
        <MenuItem
          onClick={() => {
            close();
            addToUndo(
              designPath({
                appUid,
                pageId,
              }),
              pageId,
            );
            let _page = get(pageAtom, { params: { appUid, pageId } });
            if (!_page) {
              return;
            }
            _page = { ..._page };
            const _parentId = _page[itemId].parent;
            const _parent = _page[_parentId];
            const children = [..._parent.children];
            const index = children.indexOf(itemId);
            const childItemId = replaceWithGrandChild
              ? replaceWithGrandChild.itemId
              : item.children[0];
            // replace the current item with it's child
            children[index] = childItemId;
            _page[_parentId] = {
              ..._parent,
              children,
            };
            // replace the child's parentId from current itemId to current item's parentId
            const child = { ..._page[childItemId] };
            child.parent = _parentId;
            _page[child.itemId] = child;
            // now remove the current item
            delete _page[itemId];
            if (replaceWithGrandChild) {
              delete _page[replaceWithGrandChild.parent];
            }
            getManager(_parent.type).onChildMoveIn({
              appUid,
              pageId,
              page: _page,
              oldValue: _parent,
              value: _page[_parent.itemId],
              child,
            });
            set(pageAtom, _page, { params: { appUid, pageId } });
            setActiveItem({
              appUid,
              itemId: child.itemId,
              pageId,
              expand: true,
              treename: TreeTypeDesigner,
            });
          }}
        >
          <Icon className={classes.icon} iconName="cut" color="error" />
          Delete &amp; replace with child component {replaceWithChild.name}
          <MenuItemPulse
            pulseKey="replaceWithChild"
            pulseMessage={`Use this option to delete this "${mgr.name}" and replace it with it's child "${replaceWithChild.name}".`}
          />
        </MenuItem>
      ) : null}
      {!isPage && (
        <MenuItem
          data-testid="cm-delete"
          onClick={() => {
            close();
            addToUndo(designPath({ appUid, pageId }), pageId);
            deleteItemFn({ appUid, pageId, itemId, platform });
          }}
        >
          <Icon className={classes.icon} iconName="trash" color="error" />
          Delete
        </MenuItem>
      )}
      {item.children.length && !orphan ? (
        <MenuItem
          onClick={() => {
            close();
            addToUndo(designPath({ appUid, pageId }), pageId);
            deleteChildrenActionFn({ appUid, pageId, itemId, platform });
          }}
        >
          <Icon className={classes.icon} iconName="trash-alt" color="error" />
          Delete {item.children.length} Child{' '}
          {mgr.managerType === 'Component' ? 'Component' : 'Action'}
          {item.children.length > 1 ? 's' : ''}
        </MenuItem>
      ) : null}
      {autoPopulateChildren ? (
        <MenuItem
          data-testid="cm-auto-populate-children"
          onClick={() => {
            close();
            addToUndo(designPath({ appUid, pageId }), pageId);
            autoPopulateChildrenActionFn({ appUid, platform, pageId, itemId });
          }}
        >
          <Icon className={classes.icon} iconName="wand-magic" />
          Auto Populate Children
        </MenuItem>
      ) : null}
      {allowedComponents.length ? (
        <Divider sx={{ margin: '0px !important' }} />
      ) : null}
      {/* {allowedComponents.length ? (
        <MenuItem disabled>
          <Icon className={classes.icon} iconName="plus" />
          Add
        </MenuItem>
      ) : null} */}
      <MenuItem
        disableRipple
        focusRipple={false}
        focusVisibleClassName={classes.searchFocus}
        onClick={(e) => {
          inputRef.current?.focus();
        }}
      >
        <Icon className={classes.icon} iconName="search" />
        <Input
          data-testid="cm-search"
          inputRef={inputRef}
          className={classes.search}
          value={filter ?? ''}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            e.stopPropagation();
            if (size === defaultSize && ref.current instanceof HTMLElement) {
              const box = ref.current.parentElement?.getBoundingClientRect();
              if (box) {
                setSize({ width: box.width, minHeight: box.height });
              }
            }
            setFilter(e.target.value);
          }}
          placeholder="Filter... e.g. Button or custom"
          disableUnderline
          autoFocus
        />
        <Typography>
          {filteredComponents.length !== allowedComponents.length
            ? `${filteredComponents.length} / ${allowedComponents.length}`
            : allowedComponents.length}
          {mgr.managerType === 'Component' ? ' components' : ' actions'}
        </Typography>
      </MenuItem>
      {allowedComponents.length ? (
        <Divider sx={{ margin: '0px !important' }} />
      ) : null}
      <MenuList
        ref={listRef}
        sx={{
          padding: 0,
          maxHeight: maxCCListHeight,
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        {allowedComponents.length === 0 && !filter ? (
          <MenuItem disabled>
            <Typography
              color="text.primary"
              whiteSpace="pre-wrap"
              maxWidth="320px"
            >
              No {mgr.managerType === 'Component' ? 'components' : 'actions'}{' '}
              allowed here. Use the above filter to search for{' '}
              {mgr.managerType === 'Component' ? 'components' : 'actions'} you
              are looking for.
            </Typography>
          </MenuItem>
        ) : null}
        {[
          ...filteredComponents.map((c) => {
            let iconVal = c.m.icon;
            if (typeof iconVal === 'function') {
              iconVal = iconVal({
                type: c.m.type,
                children: [],
                parent: item.itemId,
                itemId: 'x',
                props: c.m.defaultValue,
              });
            }
            return (
              <MenuItem
                data-testid={`cm-${c.m.type}`}
                sx={{ alignItems: 'flex-start' }}
                key={c.m.type}
                disabled={c.allowed !== true}
                data-tip={typeof c.allowed === 'string' ? c.allowed : undefined}
                onClick={() => {
                  close();
                  addToUndo(
                    designPath({
                      appUid,
                      pageId,
                    }),
                    pageId,
                  );
                  let _page = get(pageAtom, { params: { appUid, pageId } });
                  if (!_page) {
                    return;
                  }
                  _page = { ..._page };
                  const { page: newPage, newItemId } = createItem({
                    platform,
                    page: _page,
                    parentId: itemId,
                    itemType: c.m.type,
                    appUid,
                    pageId,
                  });
                  _page = newPage;
                  const newItem = _page[newItemId];
                  getManager(item.type).onChildMoveIn({
                    appUid,
                    pageId,
                    page: _page,
                    oldValue: item,
                    value: _page[itemId],
                    child: newItem,
                  });
                  set(pageAtom, _page, { params: { appUid, pageId } });
                  setActiveItem({
                    appUid,
                    itemId: newItem.itemId,
                    pageId,
                    expand: true,
                    treename: TreeTypeDesigner,
                  });
                }}
              >
                <ListItemIcon
                  style={{
                    minWidth: '30px',
                    marginTop: '3px',
                  }}
                >
                  <Icon {...iconVal} />
                </ListItemIcon>
                <ListItemText
                  className={classes.itemText}
                  primary={`${c.m.name}${
                    c.m.version ? ` (v${c.m.version})` : ''
                  }`}
                  secondary={
                    typeof c.allowed === 'string' ? c.allowed : undefined
                  }
                />
              </MenuItem>
            );
          }),
          filteredOtherComponents?.length ? (
            <MenuItem
              key="other_comps"
              disabled
              sx={{
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                '&.Mui-disabled': { opacity: 1 },
              }}
            >
              <Typography sx={{ whiteSpace: 'normal' }}>
                {filteredOtherComponents.length > 1
                  ? `${filteredOtherComponents.length} more ${
                      mgr.managerType === 'Component' ? 'components' : 'actions'
                    } match your search but are not allowed under ${mgr.name}.`
                  : `One more ${
                      mgr.managerType === 'Component' ? 'component' : 'action'
                    } matches your search but is not allowed under ${
                      mgr.name
                    }.`}
              </Typography>
            </MenuItem>
          ) : null,
          ...(filteredOtherComponents
            ? filteredOtherComponents.map((c) => {
                let iconVal = c.m.icon;
                if (typeof iconVal === 'function') {
                  iconVal = iconVal({
                    type: c.m.type,
                    children: [],
                    parent: item.itemId,
                    itemId: 'x',
                    props: c.m.defaultValue,
                  });
                }
                return (
                  <MenuItem
                    data-testid={`cm-${c.m.type}`}
                    sx={{
                      alignItems: 'flex-start',
                      '&.Mui-disabled': { opacity: 1 },
                    }}
                    key={c.m.type}
                    disabled
                    data-tip={
                      typeof c.allowed === 'string' ? c.allowed : undefined
                    }
                  >
                    <ListItemIcon
                      style={{
                        minWidth: '30px',
                        marginTop: '3px',
                      }}
                    >
                      <Icon {...iconVal} />
                    </ListItemIcon>
                    <ListItemText
                      className={classes.itemText}
                      primary={`${c.m.name}${
                        c.m.version ? ` (v${c.m.version})` : ''
                      }`}
                      secondary={`Allowed in: ${getAllowedParentComponents({
                        page,
                        appUid,
                        pageId,
                        platform,
                        itemType: c.m.type,
                      })
                        .map((m) => m.m.name)
                        .join(', ')}`}
                    />
                  </MenuItem>
                );
              })
            : []),
        ]}
      </MenuList>
      {allowedParentComponents.length ? (
        <Divider sx={{ margin: '0px !important' }} />
      ) : null}
      {allowedParentComponents.length ? (
        <MenuList
          key="mle"
          sx={{
            padding: 0,
            maxHeight: 140,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          {allowedParentComponents.map((c) => {
            let iconVal = c.m.icon;
            if (typeof iconVal === 'function') {
              iconVal = iconVal({
                type: c.m.type,
                children: [],
                parent: item.itemId,
                itemId: 'x',
                props: c.m.defaultValue,
              });
            }

            return (
              <MenuItem
                data-testid={`cm-embed-in-${c.m.type}`}
                sx={{ alignItems: 'flex-start' }}
                key={`embed-${c.m.type}`}
                disabled={c.allowed !== true}
                data-tip={typeof c.allowed === 'string' ? c.allowed : undefined}
                onClick={() => {
                  close();
                  addToUndo(
                    designPath({
                      appUid,
                      pageId,
                    }),
                    pageId,
                  );
                  let _page = get(pageAtom, { params: { appUid, pageId } });
                  if (!_page) {
                    return;
                  }
                  _page = { ..._page };
                  const _parentId = _page[itemId].parent;
                  const _parent = _page[_parentId];
                  // save the index of the item before removing it
                  const currentIndex = _parent.children.indexOf(itemId);
                  _page[_parentId] = {
                    ..._parent,
                    children: _parent.children.filter(
                      (_itemId) => _itemId !== itemId,
                    ),
                  };
                  const { page: newPage, newItemId } = createItem({
                    platform,
                    page: _page,
                    parentId: _parent.itemId,
                    itemType: c.m.type,
                    appUid,
                    pageId,
                  });

                  const parentItem = newPage[_parent.itemId];
                  const poppedItemId = parentItem.children.pop(); // remove the newly added item from the end
                  assert(
                    newItemId === poppedItemId,
                    `New itemId ${newItemId} is not same as the popped id ${poppedItemId}! ${parentItem.children}`,
                  );
                  // insert the new item at the same index
                  parentItem.children.splice(currentIndex, 0, newItemId);
                  const newItem = newPage[newItemId];
                  if (!c.subItem) {
                    newItem.children.forEach((_c) => {
                      deleteItem(newPage, _c);
                    });
                    newPage[newItemId] = { ...newItem, children: [itemId] };
                    newPage[itemId] = { ...newPage[itemId], parent: newItemId };
                  } else {
                    const subItemId = newItem.children.find(
                      (_c) => newPage[_c].type === c.subItem,
                    );
                    if (subItemId) {
                      const _subItem = newPage[subItemId];
                      _subItem.children.forEach((_c) => {
                        deleteItem(newPage, _c);
                      });
                      newPage[subItemId] = { ..._subItem, children: [itemId] };
                      newPage[itemId] = {
                        ...newPage[itemId],
                        parent: subItemId,
                      };
                    }
                  }
                  getManager(_parent.type).onChildMoveIn({
                    appUid,
                    pageId,
                    page: newPage,
                    oldValue: _parent,
                    value: newPage[_parent.itemId],
                    child: newItem,
                  });
                  set(pageAtom, newPage, {
                    params: {
                      appUid,
                      pageId,
                    },
                  });
                  setActiveItem({
                    appUid,
                    itemId: newItemId,
                    pageId,
                    expand: true,
                    treename: TreeTypeDesigner,
                  });
                }}
              >
                <ListItemIcon
                  style={{
                    minWidth: '30px',
                    marginTop: '3px',
                  }}
                >
                  <Icon {...iconVal} height={16} width={16} />
                </ListItemIcon>
                <ListItemText
                  className={classes.itemText}
                  primary={
                    <span>
                      <span className={classes.embed}>Embed in </span>
                      {c.m.name} {c.m.version ? `(v${c.m.version})` : null}
                    </span>
                  }
                  secondary={
                    typeof c.allowed === 'string' ? c.allowed : undefined
                  }
                />
              </MenuItem>
            );
          })}
        </MenuList>
      ) : null}
      {replaceableWithTypes.length ? (
        <Divider sx={{ margin: '0px !important' }} />
      ) : null}
      {[...replaceableWithTypes].sort().map((itemType) => {
        const m = getManager(itemType);
        let iconVal = m.icon;
        if (typeof iconVal === 'function') {
          iconVal = iconVal({
            type: m.type,
            children: [],
            parent: item.itemId,
            itemId: 'x',
            props: m.defaultValue,
          });
        }
        return (
          <MenuItem
            data-testid={`cm-replace-with-${m.type}`}
            sx={{ alignItems: 'flex-start' }}
            key={`replace-${m.type}`}
            onClick={() => {
              close();
              addToUndo(
                designPath({
                  appUid,
                  pageId,
                }),
                pageId,
              );
              replaceItem({ itemId, pageId, appUid, itemType });
            }}
          >
            <ListItemIcon
              style={{
                minWidth: '30px',
                marginTop: '3px',
              }}
            >
              <Icon {...iconVal} height={16} width={16} />
            </ListItemIcon>
            <ListItemText
              className={classes.itemText}
              primary={
                <span>
                  <span className={classes.embed}>Replace with </span>
                  {m.name} {m.version ? `(v${m.version})` : null}
                </span>
              }
            />
          </MenuItem>
        );
      })}
    </Menu>
  );
}
